/**
 * Parse markdown-link-style mentions from a string into typed segments.
 *
 * Pattern: [label](entityType:arg1:arg2:...)
 * Each entity type maps to a URL builder via MENTION_ROUTES.
 * Unknown types produce href: null.
 */

import { MentionEntityType } from '@/entities/MentionEntityType.ts';

export type TextSegment = { type: 'text'; value: string };
export type MentionSegment = {
    type: 'mention';
    label: string;
    entityType: string;
    args: string[];
    href: string | null;
};
export type ContentSegment = TextSegment | MentionSegment;

export type MentionRoutes = Record<string, (...args: string[]) => string>;

// TODO(aboutroots): mention routing is still a work in progress — only the
// `users` entity is wired so far; more entity types to follow.
const MENTION_ROUTES: MentionRoutes = {
    [MentionEntityType.USERS]: (id) => `/users/${id}`,
};

const MENTION_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Parse a content string into text and mention segments.
 *
 * `routes` is injectable so the safe-path guard can be exercised in isolation;
 * production always uses the default `MENTION_ROUTES`.
 */
export function parseMentions(
    input: string,
    routes: MentionRoutes = MENTION_ROUTES
): ContentSegment[] {
    if (!input) return [];

    const segments: ContentSegment[] = [];
    let lastIndex = 0;

    for (const match of input.matchAll(MENTION_REGEX)) {
        const matchStart = match.index ?? 0;

        if (matchStart > lastIndex) {
            segments.push({ type: 'text', value: input.slice(lastIndex, matchStart) });
        }

        const label = match[1];
        const rawArgs = match[2].split(':');
        const entityType = rawArgs[0];
        const args = rawArgs.slice(1);

        const routeBuilder = routes[entityType];
        // Enforce the safe-path contract at the source: only keep a relative
        // path. Vue does not sanitize `:href`, so this prevents a future
        // builder from echoing a raw arg into a scheme position (javascript:).
        const builtHref = routeBuilder ? routeBuilder(...args) : null;
        const href = builtHref?.startsWith('/') ? builtHref : null;

        segments.push({ type: 'mention', label, entityType, args, href });

        lastIndex = matchStart + match[0].length;
    }

    if (lastIndex < input.length) {
        segments.push({ type: 'text', value: input.slice(lastIndex) });
    }

    return segments;
}
